import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, SkipForward, SkipBack, X, Timer, CheckCircle2, ChevronDown, ChevronUp, Youtube, ChevronRight, ChevronLeft, Zap, Volume2, VolumeX, Clock, RotateCcw, Maximize } from 'lucide-react';
import { getRoutine, getExercise, logWorkoutStep, deleteWorkoutSession, updateWorkoutSession, getLatestExerciseLog, getLastSessionExerciseLogs, getYoutubePlaylist } from '../utils/api';
import { formatTime, parseTimeInput } from '../utils/time';
import Markdown from '../components/Markdown';

const ActiveSession = () => {
  const [sequence, setSequence] = useState([]);
  const [routineName, setRoutineName] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [globalStartTime, setGlobalStartTime] = useState(null);
  const [stepStartTime, setStepStartTime] = useState(null);
  const [globalElapsed, setGlobalElapsed] = useState(0);
  const [stepElapsed, setStepElapsed] = useState(0);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [lastLog, setLastLog] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [logData, setLogData] = useState({});
  const [allLogs, setAllLogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSheet, setShowSheet] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [youtubePlaylist, setYoutubePlaylist] = useState([]);
  const [showYoutubeOverlay, setShowYoutubeOverlay] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [floatingTimerSeconds, setFloatingTimerSeconds] = useState(0);
  const [isFloatingTimerRunning, setIsFloatingTimerRunning] = useState(false);
  const [isFloatingTimerMuted, setIsFloatingTimerMuted] = useState(false);
  const [videoElapsed, setVideoElapsed] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  const isNextDisabled = () => {
    const currentStep = sequence[currentIndex];
    if (!currentStep || currentStep.type !== 'exercise' || !currentExercise) {
      return false;
    }
    
    const config = JSON.parse(currentExercise.activity_log_config || '{}');
    const requiredFields = Object.entries(config)
      .filter(([field, enabled]) => enabled && field.toLowerCase() !== 'notes')
      .map(([field]) => field);
    
    return requiredFields.some(field => !logData[field] || logData[field].toString().trim() === '');
  };

  const nextExercise = sequence.slice(currentIndex + 1).find(s => s.type === 'exercise');
  const nextExerciseNumber = nextExercise 
    ? sequence.slice(0, sequence.indexOf(nextExercise) + 1).filter(s => s.type === 'exercise').length 
    : null;
  const nextExerciseSetSuffix = nextExercise?.setTitle ? ` (${nextExercise.setTitle.replace('Set ', '')})` : '';

  const navigate = useNavigate();
  const timerRef = useRef(null);
  const floatingTimerRef = useRef(null);
  const videoTimerRef = useRef(null);
  const tickAudioRef = useRef(null);
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);

  useEffect(() => {
    // Check if the script is already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
    
    // Define the global callback that YouTube's script will call
    // If it's already defined, we don't want to overwrite it if it's being used
    // But since this is a single-page app, it's likely fine.
    const originalOnYouTubeIframeAPIReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (originalOnYouTubeIframeAPIReady) originalOnYouTubeIframeAPIReady();
      // Force a re-render or set some "API ready" state if needed
      // But usually just having YT available is enough for the next render.
    };

    initSession();
    return () => {
      clearInterval(timerRef.current);
      clearInterval(videoTimerRef.current);
    };
  }, []);

  const toggleFullScreen = () => {
    if (!videoContainerRef.current) return;
    
    if (!document.fullscreenElement && 
        !document.webkitFullscreenElement && 
        !document.mozFullScreenElement && 
        !document.msFullscreenElement) {
      if (videoContainerRef.current.requestFullscreen) {
        videoContainerRef.current.requestFullscreen();
      } else if (videoContainerRef.current.webkitRequestFullscreen) { /* Safari */
        videoContainerRef.current.webkitRequestFullscreen();
      } else if (videoContainerRef.current.msRequestFullscreen) { /* IE11 */
        videoContainerRef.current.msRequestFullscreen();
      } else if (videoContainerRef.current.mozRequestFullScreen) { /* Firefox */
        videoContainerRef.current.mozRequestFullScreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) { /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) { /* IE11 */
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) { /* Firefox */
        document.mozCancelFullScreen();
      }
    }
  };

  const [apiReady, setApiReady] = useState(!!window.YT);
  useEffect(() => {
    if (!apiReady) {
      const interval = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setApiReady(true);
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [apiReady]);

  useEffect(() => {
    if (showYoutubeOverlay && apiReady && youtubePlaylist[currentVideoIndex]) {
      const videoId = getYouTubeID(youtubePlaylist[currentVideoIndex].url);
      
      const createPlayer = () => {
        if (!document.getElementById('youtube-player')) return;
        
        playerRef.current = new window.YT.Player('youtube-player', {
          height: '100%',
          width: '100%',
          videoId: videoId,
          host: 'https://www.youtube.com',
          playerVars: {
            'modestbranding': 1,
            'rel': 0,
            'autoplay': 1,
          },
          events: {
            'onStateChange': (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                setIsVideoPlaying(true);
              } else {
                setIsVideoPlaying(false);
              }
            }
          }
        });
      };

      if (!playerRef.current) {
        // Small delay to ensure the div 'youtube-player' is in the DOM
        setTimeout(createPlayer, 100);
      } else {
        if (typeof playerRef.current.loadVideoById === 'function') {
          playerRef.current.loadVideoById(videoId);
        } else {
          setTimeout(createPlayer, 100);
        }
      }
    }
  }, [showYoutubeOverlay, currentVideoIndex, apiReady]);

  useEffect(() => {
    if (isVideoPlaying && showYoutubeOverlay) {
      videoTimerRef.current = setInterval(() => {
        setVideoElapsed(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(videoTimerRef.current);
    }
    return () => clearInterval(videoTimerRef.current);
  }, [isVideoPlaying, showYoutubeOverlay]);

  useEffect(() => {
    if (!showYoutubeOverlay) {
      setIsVideoPlaying(false);
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    }
  }, [showYoutubeOverlay]);

  useEffect(() => {
    tickAudioRef.current = new Audio('/audio/clock-ticks-5s.m4a');
    return () => {
      if (tickAudioRef.current) {
        tickAudioRef.current.pause();
        tickAudioRef.current = null;
      }
    };
  }, []);

  const isFloatingTimerMutedRef = useRef(isFloatingTimerMuted);

  useEffect(() => {
    isFloatingTimerMutedRef.current = isFloatingTimerMuted;
  }, [isFloatingTimerMuted]);

  useEffect(() => {
    if (isFloatingTimerRunning) {
      floatingTimerRef.current = setInterval(() => {
        setFloatingTimerSeconds(prev => {
          // Play tick sound every 5 seconds (including at 0s)
          if (prev % 5 === 0 && tickAudioRef.current && !isFloatingTimerMutedRef.current) {
            tickAudioRef.current.currentTime = 0;
            tickAudioRef.current.play().catch(e => console.error("Audio play failed:", e));
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      clearInterval(floatingTimerRef.current);
    }
    return () => clearInterval(floatingTimerRef.current);
  }, [isFloatingTimerRunning]);

  // Add an effect to handle immediate mute/unmute of currently playing sound
  useEffect(() => {
    if (tickAudioRef.current) {
      if (isFloatingTimerMuted || !isFloatingTimerRunning) {
        tickAudioRef.current.pause();
      } else if (isFloatingTimerRunning && !isFloatingTimerMuted) {
        // If it should be playing (i.e., we are within a 5s window)
        // the offset is floatingTimerSeconds % 5
        const offset = floatingTimerSeconds % 5;
        if (offset < 5) {
          tickAudioRef.current.currentTime = offset;
          tickAudioRef.current.play().catch(e => console.error("Audio play failed:", e));
        }
      }
    }
  }, [isFloatingTimerMuted, isFloatingTimerRunning]);

  const initSession = async () => {
    try {
      const playlistRes = await getYoutubePlaylist();
      setYoutubePlaylist(playlistRes.data.playlist || []);
    } catch (err) {
      console.error('Failed to load playlist from API:', err);
    }

    try {
      const saved = JSON.parse(localStorage.getItem('active_session'));
      let routineRes;
      if (saved && saved.routineId) {
        routineRes = await getRoutine(saved.routineId);
      } else {
        routineRes = await getRoutine();
      }
      setRoutineName(routineRes.data.name || 'Default Routine');
      let rawSeq = routineRes.data.sequence || [];
      
      // Count total sets per exercise across the entire routine
      const totalSetsPerExercise = {};
      rawSeq.forEach(step => {
        if (step.type === 'exercise') {
          const sets = parseInt(step.sets) || 1;
          totalSetsPerExercise[step.exerciseId] = (totalSetsPerExercise[step.exerciseId] || 0) + sets;
        }
      });

      // Expansion Logic
      let expandedSeq = [];
      const currentSetPerExercise = {};
      
      rawSeq.forEach(step => {
        if (step.type === 'block') {
          // DO NOT Add a block header for the history view in workout mode
          // It's causing navigation issues. History page can reconstruct this 
          // or we can add it but with a flag to strictly ignore it in workout UI.
          
          /* 
          expandedSeq.push({
            type: 'block_header',
            id: `block-header-${step.id}`,
            title: step.title,
            blockId: step.id
          });
          */

          // Add all exercises in the block
          const blockExercises = rawSeq.filter(s => s.blockId === step.id);
          blockExercises.forEach((bEx, idx) => {
            const exerciseSets = parseInt(bEx.sets) || 1;
            const totalSets = totalSetsPerExercise[bEx.exerciseId] || exerciseSets;
            
            for (let s = 1; s <= exerciseSets; s++) {
              currentSetPerExercise[bEx.exerciseId] = (currentSetPerExercise[bEx.exerciseId] || 0) + 1;
              const globalSetNum = currentSetPerExercise[bEx.exerciseId];
              
              expandedSeq.push({
                ...bEx,
                blockName: step.title,
                setTitle: totalSets > 1 ? `Set ${globalSetNum}/${totalSets}` : null
              });
              if (bEx.type === 'exercise' && bEx.breakDuration > 0) {
                expandedSeq.push({
                  type: 'break',
                  duration: bEx.breakDuration,
                  id: `break-${bEx.id || Date.now()}-${Math.random()}-set-${globalSetNum}`,
                  title: `Break after ${bEx.title} (${globalSetNum}/${totalSets})`,
                  exerciseId: bEx.exerciseId,
                  blockName: step.title,
                  isAttached: true
                });
              }
            }
          });
          // Add the block's own break at the end
          if (step.breakDuration > 0) {
            expandedSeq.push({
              type: 'break',
              duration: step.breakDuration,
              id: `block-break-${step.id}`,
              title: `Break after ${step.title}`,
              blockName: step.title,
              isAttached: true
            });
          }
        } else if (!step.blockId) {
          // Standard exercise or break (only if not already processed as part of a block)
          if (step.type === 'exercise') {
            const exerciseSets = parseInt(step.sets) || 1;
            const totalSets = totalSetsPerExercise[step.exerciseId] || exerciseSets;
            
            for (let s = 1; s <= exerciseSets; s++) {
              currentSetPerExercise[step.exerciseId] = (currentSetPerExercise[step.exerciseId] || 0) + 1;
              const globalSetNum = currentSetPerExercise[step.exerciseId];
              
              expandedSeq.push({
                ...step,
                setTitle: totalSets > 1 ? `Set ${globalSetNum}/${totalSets}` : null
              });
              if (step.breakDuration > 0) {
                expandedSeq.push({
                  type: 'break',
                  duration: step.breakDuration,
                  id: `break-${step.id || Date.now()}-${Math.random()}-set-${globalSetNum}`,
                  title: `Break after ${step.title} (${globalSetNum}/${totalSets})`,
                  exerciseId: step.exerciseId,
                  isAttached: true
                });
              }
            }
          } else if (step.type === 'break') {
            expandedSeq.push(step);
          }
        }
      });

      // Remove the last item if it is a break, because it doesn't make sense at the end of a routine
      if (expandedSeq.length > 0 && expandedSeq[expandedSeq.length - 1].type === 'break') {
        expandedSeq.pop();
      }

      // Ensure we don't start on a block_header
      let startIndex = 0;
      while (startIndex < expandedSeq.length && (expandedSeq[startIndex].type === 'block_header' || expandedSeq[startIndex].type === 'block')) {
        startIndex++;
      }
      
      // If everything is a block or block_header (should not happen), reset to 0
      if (startIndex >= expandedSeq.length) startIndex = 0;

      setSequence(expandedSeq);
      
      // If we are restoring, we should also make sure we are not on a block_header
      let restoredIndex = saved && saved.currentIndex !== undefined ? saved.currentIndex : startIndex;
      
      // Safety check: if restoredIndex is out of bounds, use startIndex
      if (restoredIndex >= expandedSeq.length) restoredIndex = startIndex;

      while (restoredIndex < expandedSeq.length && (expandedSeq[restoredIndex].type === 'block_header' || expandedSeq[restoredIndex].type === 'block')) {
        restoredIndex++;
      }
      // If we skipped everything, go back to last valid step if possible
      if (restoredIndex >= expandedSeq.length && expandedSeq.length > 0) {
        restoredIndex = expandedSeq.length - 1;
        while (restoredIndex > 0 && (expandedSeq[restoredIndex].type === 'block_header' || expandedSeq[restoredIndex].type === 'block')) {
          restoredIndex--;
        }
      }

      // Restore from LocalStorage
      if (saved && saved.globalStartTime) {
        // Check if the sequence in localStorage matches the expanded sequence
        // Actually, if we just expanded it, the indices might have shifted if the routine changed.
        // But usually, the session is started with a snapshot of the routine.
        
        setGlobalStartTime(saved.globalStartTime);
        setCurrentIndex(restoredIndex);
        const restoredLogs = saved.allLogs || {};
        setAllLogs(restoredLogs);
        setLogData(restoredLogs[saved.currentIndex] || saved.logData || {});
        // Automatically resume if it was paused when entering workout mode
        if (saved.isPaused) {
          const now = Date.now();
          const pausedDuration = now - (saved.pausedAt || now);
          const totalEffectivePausedTime = (saved.totalPausedTime || 0) + pausedDuration;
          
          setTotalPausedTime(totalEffectivePausedTime);
          setIsPaused(false);
          setPausedAt(null);
          
          if (saved.globalStartTime) {
            const elapsed = Math.floor((now - saved.globalStartTime - totalEffectivePausedTime) / 1000);
            setGlobalElapsed(elapsed > 0 ? elapsed : 0);
          }
        } else {
          setIsPaused(false);
          setPausedAt(null);
          setTotalPausedTime(saved.totalPausedTime || 0);
          
          if (saved.globalStartTime) {
            const elapsed = Math.floor((Date.now() - saved.globalStartTime - (saved.totalPausedTime || 0)) / 1000);
            setGlobalElapsed(elapsed > 0 ? elapsed : 0);
          } else {
            setGlobalElapsed(saved.globalElapsed || 0);
          }
        }
        
        setStepElapsed(saved.stepElapsed || 0);
        // We'll reset step timer on resume for simplicity
        setStepStartTime(Date.now());
      } else {
        startNewSession(startIndex);
      }
    } catch (error) {
      console.error('Failed to init session', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = (startIndex = 0) => {
    const now = Date.now();
    setGlobalStartTime(now);
    setStepStartTime(now);
    setCurrentIndex(startIndex);
    const routineId = JSON.parse(localStorage.getItem('active_session'))?.routineId;
    localStorage.setItem('active_session', JSON.stringify({ 
      routineId,
      globalStartTime: now, 
      currentIndex: startIndex 
    }));
    window.dispatchEvent(new Event('storage'));
  };

  useEffect(() => {
    let timer;
    if (globalStartTime && !showFinishModal) {
      timer = setInterval(() => {
        const now = Date.now();
        let currentTotalPausedTime = totalPausedTime;
        if (isPaused && pausedAt) {
          currentTotalPausedTime += (now - pausedAt);
        }

        const elapsed = Math.floor((now - globalStartTime - currentTotalPausedTime) / 1000);
        setGlobalElapsed(elapsed > 0 ? elapsed : 0);
        
        // Auto-expiration logic: 8 hours = 28800 seconds
        if (elapsed > 28800) {
          clearInterval(timer);
          handleFinishConfirm(false); // finish without saving
          return;
        }
        
        if (!isPaused) {
          setStepElapsed(prev => {
            const currentStep = sequence[currentIndex];
            if (currentStep?.type === 'break' && currentStep.duration) {
              return Math.min(prev + 1, parseInt(currentStep.duration));
            }
            return prev + 1;
          });
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [globalStartTime, sequence, currentIndex, showFinishModal, isPaused, pausedAt, totalPausedTime]);


  useEffect(() => {
    if (sequence[currentIndex]?.type === 'exercise') {
      fetchExercise(sequence[currentIndex].exerciseId);
    } else {
      setCurrentExercise(null);
      setLastLog(null);
    }
  }, [currentIndex, sequence]);

  useEffect(() => {
    // Update LocalStorage on step change or data change
    if (globalStartTime) {
      localStorage.setItem('active_session', JSON.stringify({ 
        routineId: JSON.parse(localStorage.getItem('active_session'))?.routineId,
        globalStartTime, 
        currentIndex,
        logData,
        allLogs,
        isPaused,
        pausedAt,
        totalPausedTime,
        globalElapsed,
        stepElapsed
      }));
      window.dispatchEvent(new Event('storage'));
    }
  }, [currentIndex, sequence, isPaused, pausedAt, totalPausedTime, globalElapsed, stepElapsed, allLogs, logData, globalStartTime]);

  const fetchExercise = async (id, currentLogs = null) => {
    try {
      const res = await getExercise(id);
      setCurrentExercise(res.data);
      
      const lastLogsRes = await getLastSessionExerciseLogs(id, globalStartTime);
      const lastLogs = Array.isArray(lastLogsRes.data) ? lastLogsRes.data : [];
      
      // Get current exercise position among other instances of the same exercise
      const currentStep = sequence[currentIndex];
      let instanceIndex = 0;
      for (let i = 0; i < currentIndex; i++) {
        if (sequence[i].type === 'exercise' && sequence[i].exerciseId == id) {
          instanceIndex++;
        }
      }
      
      const fetchedLastLog = lastLogs[instanceIndex] || (lastLogs.length > 0 ? lastLogs[lastLogs.length - 1] : null);
      
      // Fallback: If no historical log was found (e.g., new exercise), 
      // check if we have already logged this exercise in the current session
      let currentSessionLastLog = null;
      if (!fetchedLastLog) {
        const logsToSearch = currentLogs || allLogs;
        
        // Find all previous logs for this exercise in the current session
        const previousExerciseLogs = [];
        const sortedIndices = Object.keys(logsToSearch)
          .map(Number)
          .filter(idx => idx < currentIndex)
          .sort((a, b) => a - b); // Chronological order

        for (const idx of sortedIndices) {
          if (sequence[idx]?.exerciseId == id || (logsToSearch[idx] && logsToSearch[idx].exerciseId == id)) {
            previousExerciseLogs.push({ data: logsToSearch[idx] });
          }
        }

        // Match the current instance index with the previous logs in the current session
        // If we have 3 instances and we are at instance 2 (0-indexed), 
        // we check if we have already logged instance 0 and 1.
        if (previousExerciseLogs.length > 0) {
          // If we have multiple previous logs, try to match by index, otherwise take the last one
          currentSessionLastLog = previousExerciseLogs[instanceIndex] || previousExerciseLogs[previousExerciseLogs.length - 1];
        }
      }

      const finalLastLog = fetchedLastLog || currentSessionLastLog;
      setLastLog(finalLastLog);

      // Pre-fill logData ONLY if it's empty and we have a last log
      setLogData(prevLogData => {
        const keys = Object.keys(prevLogData);
        // We only pre-fill if logData is absolutely empty.
        // If it was already set (e.g. from restored session), we don't touch it.
        if (keys.length === 0 && finalLastLog && finalLastLog.data) {
          const { exerciseId, ...restOfData } = finalLastLog.data;
          return { ...restOfData };
        }
        return prevLogData;
      });
    } catch (error) {
      console.error('Failed to fetch exercise or logs', error);
      setLastLog(null);
    }
  };

  const handleNext = async () => {
    const currentStep = sequence[currentIndex];
    
    // Save current logData to allLogs
    const updatedLogs = { ...allLogs, [currentIndex]: { ...logData, exerciseId: currentStep.exerciseId } };
    setAllLogs(updatedLogs);

    // Log current step (exercise or break)
    const normalizedData = {};
    if (currentStep.type === 'exercise') {
      Object.entries(logData).forEach(([key, value]) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey === 'time') {
          normalizedData[lowerKey] = parseTimeInput(value);
        } else {
          normalizedData[lowerKey] = value;
        }
      });
    }

    await logWorkoutStep({
      exercise_id: currentStep.type === 'exercise' ? currentStep.exerciseId : null,
      data: normalizedData,
      step_duration: stepElapsed,
      global_session_id: globalStartTime.toString()
    });

    if (currentIndex < sequence.length - 1) {
      const nextIndex = currentIndex + 1;
      
      // Auto-skip block headers and block titles in workout mode
      // These are only for tracking/history purposes
      let finalIndex = nextIndex;
      while (finalIndex < sequence.length && (sequence[finalIndex].type === 'block_header' || sequence[finalIndex].type === 'block')) {
        finalIndex++;
      }
      
      if (finalIndex < sequence.length) {
        setCurrentIndex(finalIndex);
        setStepStartTime(Date.now());
        setStepElapsed(0);
        // Reset logData for the next step so fetchExercise can pre-fill it correctly
        setLogData(updatedLogs[finalIndex] || {});
        setShowSheet(false);
        resetFloatingTimer();
        
        // Trigger fetchExercise immediately with updatedLogs to ensure fallback pre-fill works
        if (sequence[finalIndex]?.type === 'exercise') {
          fetchExercise(sequence[finalIndex].exerciseId, updatedLogs);
        }
      } else {
        finishSession();
      }
    } else {
      finishSession();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      let prevIndex = currentIndex - 1;
      
      // Auto-skip block headers and block titles in workout mode when going back
      while (prevIndex > 0 && (sequence[prevIndex].type === 'block_header' || sequence[prevIndex].type === 'block')) {
        prevIndex--;
      }

      // Save current logData to allLogs
      const currentStep = sequence[currentIndex];
      const updatedLogs = { ...allLogs, [currentIndex]: { ...logData, exerciseId: currentStep.exerciseId } };
      setAllLogs(updatedLogs);
      
      setCurrentIndex(prevIndex);
      setStepStartTime(Date.now());
      setStepElapsed(0);
      // Reset logData for the previous step so fetchExercise can pre-fill it correctly or restore it
      setLogData(updatedLogs[prevIndex] || {});
      setShowSheet(false);
      resetFloatingTimer();

      // Trigger fetchExercise immediately with updatedLogs to ensure fallback pre-fill works
      if (sequence[prevIndex]?.type === 'exercise') {
        fetchExercise(sequence[prevIndex].exerciseId, updatedLogs);
      }
    }
  };

  const finishSession = () => {
    setShowFinishModal(true);
  };

  const handleFinishConfirm = async (save) => {
    // Session expiration logic: 8 hours = 8 * 3600 = 28800 seconds
    const isExpired = globalElapsed > 28800;
    
    if (!save || isExpired) {
      await deleteWorkoutSession(globalStartTime.toString());
      if (isExpired) {
        alert("Session expired (longer than 8 hours) and was not saved.");
      }
    } else {
      // Update the first record of the session with the actual global elapsed time
      // The backend SUM(step_duration) will then reflect this total
      
      // Filter out meta steps from the sequence saved to history to avoid confusing users
      // but keep them if we want to show headers. Actually History.js uses them, 
      // so we MUST keep them.
      await updateWorkoutSession(globalStartTime.toString(), {
        total_duration: globalElapsed,
        routine_name: routineName,
        routine_sequence: JSON.stringify(sequence)
      });
    }
    localStorage.removeItem('active_session');
    window.dispatchEvent(new Event('storage'));
    setShowFinishModal(false);
    navigate('/');
  };


  const handleLogChange = (field, value) => {
    setLogData({ ...logData, [field]: value });
  };

  const togglePause = () => {
    const now = Date.now();
    if (isPaused) {
      // Resuming
      const pausedDuration = now - pausedAt;
      setTotalPausedTime(prev => prev + pausedDuration);
      setIsPaused(false);
      setPausedAt(null);
    } else {
      // Pausing
      setIsPaused(true);
      setPausedAt(now);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#121214] flex items-center justify-center">Loading routine...</div>;
  if (sequence.length === 0) return <div className="min-h-screen bg-[#121214] flex items-center justify-center">No routine found.</div>;

  const currentStep = sequence[currentIndex];
  
  if (!currentStep || currentStep.type === 'block_header' || currentStep.type === 'block') {
    return (
      <div className="min-h-screen bg-[#121214] text-white flex flex-col items-center justify-center p-6">
        <div className="animate-pulse text-slate-500 font-black uppercase tracking-widest">
          Transitioning...
        </div>
      </div>
    );
  }

  const exercisesOnly = sequence.filter(s => s.type !== 'break' && s.type !== 'block_header' && s.type !== 'block');

  const getYouTubeID = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const toggleFloatingTimer = () => {
    setIsFloatingTimerRunning(!isFloatingTimerRunning);
  };

  const resetFloatingTimer = () => {
    setIsFloatingTimerRunning(false);
    setFloatingTimerSeconds(0);
  };

  return (
    <div className="min-h-screen bg-[#121214] text-white flex flex-col relative overflow-hidden">
      {/* Youtube Bookmark Button */}
      {youtubePlaylist.length > 0 && !showYoutubeOverlay && (
        <button
          onClick={() => setShowYoutubeOverlay(true)}
          className="fixed right-0 top-1/2 -translate-y-[calc(50%+45px)] z-40 bg-[#A3E635] text-black pl-4 pr-2 py-6 rounded-l-[32px] shadow-[0_0_30px_rgba(163,230,53,0.3)] hover:pl-6 transition-all duration-300 group flex items-center justify-center"
        >
          <div className="relative flex items-center justify-center">
            <Youtube size={32} fill="black" className="group-hover:scale-110 transition-transform" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-[#A3E635] border-b-[6px] border-b-transparent ml-1"></div>
            </div>
          </div>
        </button>
      )}

      {/* Youtube Overlay */}
      {showYoutubeOverlay && youtubePlaylist.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col animate-in fade-in zoom-in duration-300">
          <div className="p-6 flex items-center justify-between bg-black/50 backdrop-blur-md">
             <div className="flex items-center gap-4">
                <button 
                  onClick={() => setShowYoutubeOverlay(false)}
                  className="p-2 rounded-full bg-[#1C1C1E] text-slate-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
                <div>
                   <h3 className="text-sm font-black uppercase tracking-widest text-[#A3E635]">Entertainment</h3>
                   <p className="text-xs font-bold text-slate-400 truncate max-w-[200px] md:max-w-md">
                      {youtubePlaylist[currentVideoIndex]?.title || 'Video'}
                   </p>
                </div>
             </div>

             <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentVideoIndex(prev => (prev > 0 ? prev - 1 : youtubePlaylist.length - 1))}
                  className="p-3 rounded-full bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <span className="text-xs font-black tabular-nums min-w-[3rem] text-center">
                   {currentVideoIndex + 1} / {youtubePlaylist.length}
                </span>
                <button 
                  onClick={() => setCurrentVideoIndex(prev => (prev < youtubePlaylist.length - 1 ? prev + 1 : 0))}
                  className="p-3 rounded-full bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] transition-colors"
                >
                  <ChevronRight size={24} />
                </button>
             </div>
          </div>

          <div className="flex-1 w-full bg-black relative" ref={videoContainerRef}>
            <div id="youtube-player" className="absolute inset-0 w-full h-full"></div>
            
            {/* Pseudo Fullscreen Button - Overlays native YouTube fullscreen button (bottom right) */}
            <div 
              className="absolute bottom-0 right-0 w-12 h-12 z-[2147483647] cursor-pointer"
              onClick={toggleFullScreen}
              title="Toggle Fullscreen"
            ></div>
            
            {/* Session Timer Overlay on Video */}
            <div className="absolute bottom-4 left-4 z-[2147483647]">
              <div className="bg-black/70 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 flex items-center gap-2">
                <Clock size={16} className="text-[#A3E635]" />
                <span className="text-xl font-black italic tracking-tighter tabular-nums text-white">
                  {formatTime(videoElapsed)}
                </span>
                <button 
                  onClick={() => setVideoElapsed(0)}
                  className="ml-1 p-1 rounded-full text-slate-400 hover:text-[#A3E635] transition-colors"
                  title="Reset timer"
                >
                  <RotateCcw size={14} />
                </button>
                <div className="w-[1px] h-4 bg-white/20 mx-1"></div>
                <button 
                  onClick={toggleFullScreen}
                  className="p-1 rounded-full text-slate-400 hover:text-[#A3E635] transition-colors"
                  title="Toggle Fullscreen"
                >
                  <Maximize size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header / Progress */}
      <div className="p-6 flex items-center justify-between border-b border-[#1C1C1E]">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/routine')} className="text-slate-500 hover:text-white"><X size={24} /></button>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-black uppercase tracking-widest text-[#A3E635]">Next Exercise</span>
          <div className="flex items-center gap-4 mt-1">
             <span className="text-xs font-bold uppercase italic tracking-tighter">
               {nextExercise 
                 ? `${nextExerciseNumber} / ${exercisesOnly.length} : ${nextExercise.title}${nextExerciseSetSuffix}`
                 : "Last Step"
               }
             </span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">Total Time</span>
          <span className="text-xl font-black italic tracking-tighter tabular-nums">{formatTime(globalElapsed)}</span>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6 max-w-lg mx-auto w-full">
        {currentStep.type === 'break' ? (
          <div className="text-center space-y-8">
            {(() => {
              const isDone = stepElapsed >= parseInt(currentStep.duration || 0);
              const colorClass = isDone ? 'text-red-500' : 'text-blue-500';
              const borderColorClass = isDone ? 'border-red-500/20' : 'border-blue-500/20';
              const ringColorClass = isDone ? 'border-red-500' : 'border-blue-500';

              return (
                <div className={`w-48 h-48 rounded-full border-8 ${borderColorClass} flex items-center justify-center relative`}>
                  <Timer size={64} className={`${colorClass} absolute opacity-10`} />
                  <span className="text-6xl font-black italic tracking-tighter tabular-nums">{stepElapsed}s</span>
                  <div className={`absolute inset-0 rounded-full border-t-8 ${ringColorClass} ${(!isDone && !isPaused) ? 'animate-spin-slow' : (isPaused && !isDone) ? 'animate-spin-slow animate-paused' : ''}`}></div>
                </div>
              );
            })()}
            <div>
              <h2 className="text-2xl font-black italic tracking-tighter uppercase mb-2">
                {currentStep.blockName ? `${currentStep.blockName} - ` : ''}Break Time
              </h2>
              <p className="text-2xl text-slate-500 font-bold uppercase tracking-widest">Planned: {currentStep.duration}s</p>
            </div>
          </div>
        ) : (
          <div className="w-full space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-black italic tracking-tighter uppercase mb-2 leading-tight flex flex-wrap items-center justify-center gap-2">
                <span>{currentStep.blockName ? `${currentStep.blockName} - ` : ''}{currentStep.title}</span>
                {currentStep.setTitle && (
                  <span className="text-[#A3E635] text-sm not-italic font-black bg-[#A3E635]/10 px-2 py-1 rounded-full border border-[#A3E635]/20">
                    {currentStep.setTitle}
                  </span>
                )}
              </h2>
              {currentExercise?.subtitle && (
                <p className="text-2xl text-[#A3E635] font-black uppercase tracking-widest mb-4">
                  {currentExercise.subtitle}
                </p>
              )}
            </div>

            {/* Logging Inputs */}
            {currentExercise && (
              <div className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E] space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(JSON.parse(currentExercise.activity_log_config || '{}')).filter(([_, v]) => v).map(([field]) => {
                    const isTimeField = field.toLowerCase() === 'time';
                    return (
                      <div key={field}>
                        <label className="block text-[10px] text-slate-500 font-black uppercase mb-2 tracking-widest">{field}</label>
                        <input
                          type={isTimeField ? "text" : "number"}
                          placeholder={isTimeField ? "00:00" : "0"}
                          value={logData[field] || ''}
                          onChange={(e) => handleLogChange(field, e.target.value)}
                          className="w-full bg-[#2C2C2E] border-none rounded-2xl px-6 py-4 text-xl font-black text-center focus:ring-2 ring-[#A3E635] outline-none transition-all"
                        />
                      </div>
                    );
                  })}
                </div>
                
                {/* Minimal Stopwatch */}
                <div className="flex items-center justify-between pt-2 border-t border-[#2C2C2E]/50">
                  <div className="flex items-center gap-2">
                    <Zap size={14} className={isFloatingTimerRunning ? "text-[#A3E635]" : "text-slate-600"} />
                    <span className="text-xl font-black italic tracking-tighter tabular-nums w-16">
                      {formatTime(floatingTimerSeconds)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleFloatingTimer}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                        isFloatingTimerRunning 
                          ? 'bg-red-500/10 text-red-500' 
                          : 'bg-[#A3E635] text-black'
                      }`}
                    >
                      {isFloatingTimerRunning ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="black" />}
                    </button>
                    
                    <button
                      onClick={() => setIsFloatingTimerMuted(!isFloatingTimerMuted)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                        isFloatingTimerMuted 
                          ? 'bg-white/5 text-slate-400' 
                          : 'bg-[#A3E635]/20 text-[#A3E635]'
                      }`}
                      title={isFloatingTimerMuted ? "Unmute" : "Mute"}
                    >
                      {isFloatingTimerMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    </button>

                    <button
                      onClick={resetFloatingTimer}
                      className="w-8 h-8 rounded-full bg-white/5 text-slate-400 flex items-center justify-center hover:bg-white/10 transition-colors"
                    >
                      <RotateCcw size={14} />
                    </button>
                  </div>
                </div>

                {currentExercise.description && currentExercise.description.trim() !== '' && (
                  <>
                    <button 
                      onClick={() => setShowSheet(!showSheet)}
                      className="w-full flex items-center justify-center gap-2 text-slate-500 font-bold uppercase text-[10px] hover:text-white transition-colors"
                    >
                      {showSheet ? <ChevronUp size={16} /> : <ChevronDown size={16} />} 
                      {showSheet ? 'Hide technical sheet' : 'View technical sheet'}
                    </button>

                    {showSheet && (
                      <div className="pt-6 border-t border-[#2C2C2E] flex flex-col gap-6">
                        <div className="max-h-60 overflow-y-auto">
                          <Markdown content={currentExercise.description} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Controls */}
      <footer className="p-8 flex flex-col items-center gap-6 max-w-lg mx-auto w-full">
        {isNextDisabled() && (
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-red-500/60 animate-pulse">
            Fill all fields to continue
          </div>
        )}
        <div className="flex items-center justify-center gap-12 w-full">
          <button 
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="w-20 h-20 rounded-full bg-[#1C1C1E] text-slate-400 flex items-center justify-center hover:bg-[#2C2C2E] transition-colors disabled:opacity-20 shadow-xl"
          >
            <SkipBack size={32} />
          </button>

          <button 
            onClick={handleNext}
            disabled={isNextDisabled()}
            className={`w-24 h-24 rounded-full flex items-center justify-center transition-all shadow-[0_0_40px_rgba(163,230,53,0.3)] ${
              isNextDisabled() 
              ? 'bg-[#1C1C1E] text-slate-700 opacity-50 cursor-not-allowed scale-90' 
              : 'bg-[#A3E635] text-black hover:scale-105'
            }`}
          >
            {currentIndex === sequence.length - 1 ? <CheckCircle2 size={40} /> : <SkipForward size={40} />}
          </button>
        </div>
      </footer>

      {/* Completion Modal */}
      {showFinishModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[40px] p-10 max-w-sm w-full text-center space-y-8 animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-[#A3E6351A] text-[#A3E635] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={40} />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-3xl font-black italic tracking-tighter uppercase">Workout Complete!</h3>
              <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">
                Total Time: <span className="text-white">{formatTime(globalElapsed)}</span>
              </p>
            </div>

            <p className="text-slate-400 font-medium">
              Would you like to add this session in the Session History?
            </p>

            <div className="grid gap-3">
              <button 
                onClick={() => handleFinishConfirm(true)}
                className="w-full bg-[#A3E635] text-black py-4 rounded-full font-black uppercase tracking-widest hover:scale-105 transition-transform"
              >
                Yes, Save Session
              </button>
              <button 
                onClick={() => handleFinishConfirm(false)}
                className="w-full bg-[#2C2C2E] text-white py-4 rounded-full font-black uppercase tracking-widest hover:bg-[#3C3C3E] transition-colors"
              >
                No, Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveSession;
